import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import { Menu, Car } from "lucide-react";
import { useI18n } from "../i18n";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { CurrencySwitcher } from "./CurrencySwitcher";
import { useBooking } from "../lib/booking-context";
import { cn } from "../lib/utils";

const links = [
  { id: "services", key: "nav.services" },
  { id: "calculator", key: "nav.calculator" },
  { id: "routes", key: "nav.routes" },
  { id: "fleet", key: "nav.fleet" },
  { id: "contacts", key: "nav.contacts" },
];

export const Header = () => {
  const { t } = useI18n();
  const { requestBooking } = useBooking();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="px-3 sm:px-4">
        <div
          className={cn(
            "mx-auto mt-3 max-w-[1180px] rounded-2xl border transition-all duration-300",
            scrolled
              ? "border-border/70 glass-strong shadow-[0_14px_50px_-18px_rgba(0,0,0,0.8)]"
              : "border-transparent bg-transparent"
          )}
        >
          <div className="flex items-center justify-between px-3 sm:px-4 h-14">
            <button onClick={() => go("top")} className="flex items-center gap-2" data-testid="brand-logo">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-[0_6px_20px_-6px_hsl(42_70%_45%/0.6)]">
                <Car className="h-5 w-5" />
              </span>
              <span className="font-display text-lg font-semibold tracking-tight">Transfer24</span>
            </button>

            <nav className="hidden lg:flex items-center gap-7">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-150"
                  data-testid={`nav-${l.id}`}
                >
                  {t(l.key)}
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-2.5">
              <CurrencySwitcher />
              <LanguageSwitcher />
              <Button onClick={() => requestBooking({})} data-testid="nav-book-transfer-button" className="rounded-xl shimmer active:scale-[0.98]">
                {t("cta.book")}
              </Button>
            </div>

            <div className="flex lg:hidden items-center gap-2">
              <CurrencySwitcher />
              <LanguageSwitcher />
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="rounded-xl border-border/70 bg-card/40" data-testid="mobile-nav-open-button" aria-label="Menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72 bg-background border-border">
                  <div className="mt-8 flex flex-col gap-1">
                    {links.map((l) => (
                      <SheetClose asChild key={l.id}>
                        <button onClick={() => go(l.id)} className="text-left px-3 py-3 rounded-lg text-base font-medium hover:bg-secondary transition-colors" data-testid={`mobile-nav-${l.id}`}>
                          {t(l.key)}
                        </button>
                      </SheetClose>
                    ))}
                    <SheetClose asChild>
                      <Button onClick={() => requestBooking({})} className="mt-4 rounded-xl shimmer" data-testid="mobile-nav-book-button">
                        {t("cta.book")}
                      </Button>
                    </SheetClose>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
